import { useMemo, useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { useGlobalStateContext } from "@/context/GlobalContext";
import { usePreviousStateContext } from "@/context/PreviousStateContext";
import { 
  UnifiedTabConfig, 
  UnifiedTabContext, 
  TabState,
  WorkflowLevel 
} from '@/types/UnifiedTab.types';
import { 
  WORKFLOW_LEVELS,
  PATH_TAB_CONFIG,
  isArchivedOrOnHold,
  getWorkflowLevelFromStatus,
  shouldEnableTabForWorkflowLevel,
  hasBusinessReviewAccess,
  isPathAllowed
} from '@/utils/unifiedTabHelpers';
import productService from "@/services/productService";

// Tab configurations
const TAB_CONFIGS: UnifiedTabConfig[] = [
  {
    id: 'product-information',
    label: 'Product Information',
    component: null as any, // Will be set by component
    enabled: () => true, // Always enabled
    order: 0
  },
  {
    id: 'vendor-information',
    label: 'Vendor Information', 
    component: null as any, // Will be set by component
    enabled: () => true, // Always enabled
    order: 1
  },
  {
    id: 'pre-panel-information',
    label: 'Pre-Panel Information',
    component: null as any, // Will be set by component
    enabled: (context: UnifiedTabContext) => {
      // Check if path allows this tab
      if (!isPathAllowed(context.pathname, PATH_TAB_CONFIG.prePanelPaths)) {
        return false;
      }
      
      // Check workflow level
      return shouldEnableTabForWorkflowLevel(
        WORKFLOW_LEVELS.prePanel.level,
        context.currentWorkflowLevel,
        WORKFLOW_LEVELS.prePanel.includesLowerLevels
      );
    },
    order: 2,
    requiresPath: PATH_TAB_CONFIG.prePanelPaths
  },
  {
    id: 'business-review',
    label: 'Business Review',
    component: null as any, // Will be set by component
    enabled: (context: UnifiedTabContext) => {
      // Check if path allows this tab
      if (!isPathAllowed(context.pathname, PATH_TAB_CONFIG.businessReviewPaths)) {
        return false;
      }
      
      // Check workflow level
      return shouldEnableTabForWorkflowLevel(
        WORKFLOW_LEVELS.businessReview.level,
        context.currentWorkflowLevel,
        WORKFLOW_LEVELS.businessReview.includesLowerLevels
      );
    },
    order: 3,
    requiresPath: PATH_TAB_CONFIG.businessReviewPaths
  },
  {
    id: 'tasting-panel',
    label: 'Tasting Panel',
    component: null as any, // Will be set by component
    enabled: (context: UnifiedTabContext) => {
      // Check if path allows this tab
      if (!isPathAllowed(context.pathname, PATH_TAB_CONFIG.tastingPanelPaths)) {
        return false;
      }
      
      // Check workflow level
      return shouldEnableTabForWorkflowLevel(
        WORKFLOW_LEVELS.tastingPanel.level,
        context.currentWorkflowLevel,
        WORKFLOW_LEVELS.tastingPanel.includesLowerLevels
      );
    },
    order: 4,
    requiresPath: PATH_TAB_CONFIG.tastingPanelPaths
  }
];

export const useUnifiedTabConfiguration = (selectedVendorId: string) => {
  const pathname = usePathname();
  const { selectedActiveProject } = useGlobalStateContext();
  const { getPreviousStateByVendor, updatePreviousStateFromResponse, previousStateList } = usePreviousStateContext() as any;
  const [hydrating, setHydrating] = useState(false);

  // On-demand hydrate previous state from list APIs when missing
  useEffect(() => {
    const prev = getPreviousStateByVendor(selectedVendorId);
    if (prev || hydrating) return;

    const isShoppingList = pathname.includes('shopping-list-management');
    if (isShoppingList) {
      setHydrating(true);
      (async () => {
        try {
          // const resp = await productService.fetchProducts();
          // updatePreviousStateFromResponse(resp, 'shoppinglist');
        } catch (e) {
          // silent fail; leave previous state empty
        } finally {
          setHydrating(false);
        }
      })();
    }
  }, [pathname, selectedVendorId, getPreviousStateByVendor, updatePreviousStateFromResponse, hydrating]);

  const tabContext: UnifiedTabContext = useMemo(() => {
    const vendorStatusValue = (selectedActiveProject?.status || '').toLowerCase();
    const isArchivedOrOnHoldStatus = isArchivedOrOnHold(vendorStatusValue);
    const previousState = getPreviousStateByVendor(selectedVendorId)?.previousState || null;
    const currentWorkflowLevel = getWorkflowLevelFromStatus(
      vendorStatusValue,
      previousState,
      isArchivedOrOnHoldStatus
    );

    return {
      pathname,
      vendorId: selectedVendorId,
      vendorStatus: vendorStatusValue,
      previousState,
      isArchivedOrOnHold: isArchivedOrOnHoldStatus,
      currentWorkflowLevel,
      hasBusinessReviewAccess: false
    };
  }, [pathname, selectedVendorId, selectedActiveProject?.status, getPreviousStateByVendor, previousStateList]);

  const enabledTabs: TabState[] = useMemo(() => {
    return TAB_CONFIGS
      .map(tab => {
        const enabled = tab.enabled(tabContext);
        return {
          id: tab.id,
          label: tab.label,
          enabled,
          disabled: !enabled,
          order: tab.order
        };
      })
      .filter(tab => tab.enabled)
      .sort((a, b) => a.order - b.order);
  }, [tabContext]);

  const isTabEnabled = (tabId: string): boolean => {
    const tab = enabledTabs.find(t => t.id === tabId);
    return tab?.enabled || false;
  };

  const getTabState = (tabId: string): TabState | null => {
    return enabledTabs.find(t => t.id === tabId) || null;
  };

  return {
    enabledTabs,
    isTabEnabled,
    getTabState,
    tabContext
  };
};
